import { preventImplicitSubmit } from "../keyboard/form_keys"
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static targets = [
        "form",
        "client",
        "requestedDate",
        "itemsPanel",
        "itemsContainer",
        "template",
        "source",
        "location",
        "contact",
        "contactDetails",
        "profile",
        "summaryItems",
        "summarySubtotal",
        "summaryDeliveryFee",
        "summaryTax",
        "summaryTotal"
    ]

    static values = {
        optionsUrl: String,
        clients: Object,
        listings: Object,
        currency: String,
        deliveryFeeAmount: Number,
        freeShippingThreshold: Number
    }

    connect() {
        this.itemContent = this.hasTemplateTarget ? this.templateTarget.innerHTML : ""
        this.formTarget.addEventListener("keydown", this.preventEnterSubmit)
        this.toggleItemsPanel()
        this.ensureSingleBlankItem()
        this.syncAllRows()
        this.refreshClientPanels()
        this.refreshSummary()

        this.schedulePrimaryFocus()
    }

    disconnect() {
        this.formTarget.removeEventListener("keydown", this.preventEnterSubmit)
    }

    preventEnterSubmit = preventImplicitSubmit

    async loadOption(kind, id) {
        const url = new URL(this.optionsUrlValue, window.location.origin)
        url.searchParams.set("kind", kind)
        url.searchParams.set("id", id)
        const response = await fetch(url, { headers: { Accept: "application/json" }, credentials: "same-origin" })
        if (!response.ok) throw new Error("Unable to load the selected record")
        return response.json()
    }

    async changeClient() {
        const id = this.clientField()?.value
        if (id && !this.clientsValue[id]) {
            try {
                const data = await this.loadOption("clients", id)
                if (!this.element.isConnected || this.clientField()?.value !== id) return
                this.clientsValue = { ...this.clientsValue, [id]: data }
            } catch (error) { console.error(error); return }
        }
        this.toggleItemsPanel()
        this.ensureSingleBlankItem()
        this.refreshClientPanels()
        this.refreshSummary()
        this.schedulePrimaryFocus()
    }

    changeRequestedDate() {
        this.refreshSummary()
    }

    addItem(event) {
        if (event) event.preventDefault()
        if (!this.clientSelected() || !this.hasTemplateTarget || !this.hasItemsContainerTarget) {
            this.clientDisplayField()?.focus()
            return
        }

        const item = this.insertBlankItem()
        this.syncRow(item)
        this.refreshSummary()
        item.querySelector("input, select")?.focus()
    }

    removeItem(event) {
        event.preventDefault()
        const item = event.target.closest(".item-container")
        if (!item) return

        item.remove()
        this.ensureSingleBlankItem()
        this.refreshSummary()
    }

    async syncLineItem(event) {
        const item = event.target.closest(".item-container")
        if (!item) return

        const id = this.listingField(item)?.value
        if (id && !this.listingsValue[id]) {
            try {
                const data = await this.loadOption("listings", id)
                if (!item.isConnected || this.listingField(item)?.value !== id) return
                this.listingsValue = { ...this.listingsValue, [id]: data }
            } catch (error) { console.error(error); return }
        }
        this.syncRow(item)
        this.ensureTrailingBlankRow(item)
        this.refreshSummary()
    }

    quantityChanged() {
        this.refreshSummary()
    }

    unitPriceChanged() {
        this.refreshSummary()
    }

    toggleItemsPanel() {
        if (!this.hasItemsPanelTarget) return

        this.itemsPanelTarget.classList.toggle("d-none", !this.clientSelected())
    }

    ensureSingleBlankItem() {
        if (!this.clientSelected() || !this.hasItemsContainerTarget || !this.hasTemplateTarget) return

        const items = this.items()
        if (items.length === 0) {
            this.insertBlankItem()
            return
        }

        const blankItems = items.filter(item => this.itemIsBlank(item))
        if (blankItems.length === 0) {
            this.insertBlankItem()
            return
        }

        blankItems.slice(1).forEach(item => item.remove())
    }

    insertBlankItem() {
        if (!this.itemContent) return null

        const content = this.itemContent.replace(/NEW_RECORD/g, new Date().getTime())
        this.itemsContainerTarget.insertAdjacentHTML("beforeend", content)
        return this.itemsContainerTarget.lastElementChild
    }

    items() {
        return Array.from(this.itemsContainerTarget.querySelectorAll("li.item-container"))
    }

    itemIsBlank(item) {
        return !this.listingField(item)?.value && !this.quantityField(item)?.value && !this.unitPriceField(item)?.value
    }

    syncAllRows() {
        if (!this.hasItemsContainerTarget) return

        this.items().forEach(item => this.syncRow(item))
    }

    syncRow(item) {
        const listing = this.selectedListing(item)
        const unitPriceInput = this.unitPriceField(item)
        const previousListingId = item.dataset.listingId || ""

        if (!listing) {
            item.dataset.listingId = ""
            if (unitPriceInput && previousListingId.length > 0) unitPriceInput.value = ""
        } else if (unitPriceInput && previousListingId !== String(this.listingField(item)?.value || "")) {
            unitPriceInput.value = listing.price_value || ""
            item.dataset.listingId = String(this.listingField(item)?.value || "")
        }
    }

    ensureTrailingBlankRow(item) {
        if (!this.selectedListing(item)) return
        if (item !== this.itemsContainerTarget.lastElementChild) return
        if (this.items().some(existingItem => existingItem !== item && this.itemIsBlank(existingItem))) return

        this.insertBlankItem()
    }

    refreshClientPanels() {
        const summary = this.selectedClientSummary()

        this.updateTargetText("source", summary?.source || "Select client")
        this.updateTargetText("location", summary?.default_location || "Not set")
        this.updateTargetText("contact", summary?.main_contact || "Not set")
        this.updateTargetText("contactDetails", summary?.contact_details || "Details not set")
        this.updateTargetText("profile", summary?.linked_buyer_profile || "Will be created automatically")
    }

    refreshSummary() {
        const selectedItems = this.hasItemsContainerTarget ? this.items().filter(item => this.selectedListing(item)) : []
        const subtotal = selectedItems.reduce((sum, item) => {
            const listing = this.selectedListing(item)
            const quantity = Number(this.quantityField(item)?.value || 0)
            return sum + (this.currentUnitPrice(item, listing) * quantity)
        }, 0)
        const deliveryFee = this.currentDeliveryFee(subtotal)
        const tax = 0
        const total = subtotal + deliveryFee + tax

        this.updateTargetText("summaryItems", String(selectedItems.length))
        this.updateTargetText("summarySubtotal", this.formatCurrency(subtotal))
        this.updateTargetText("summaryDeliveryFee", this.formatCurrency(deliveryFee))
        this.updateTargetText("summaryTax", this.formatCurrency(tax))
        this.updateTargetText("summaryTotal", this.formatCurrency(total))
    }

    selectedClientSummary() {
        return this.clientsValue[this.clientField()?.value] || null
    }

    selectedClientLabel() {
        return this.clientDisplayField()?.value || ""
    }

    selectedListing(item) {
        return this.listingsValue[this.listingField(item)?.value] || null
    }

    clientSelected() {
        return Boolean(this.clientField()?.value)
    }

    clientField() {
        if (!this.hasClientTarget) return null
        if (this.clientTarget.matches("input, select")) return this.clientTarget

        return this.clientTarget.querySelector("[data-hw-combobox-target='hiddenField']")
    }

    clientDisplayField() {
        if (!this.hasClientTarget) return null
        if (this.clientTarget.matches("input, select")) return this.clientTarget

        return this.clientTarget.querySelector("[role='combobox']")
    }

    requestedDateField() {
        return this.hasRequestedDateTarget ? this.requestedDateTarget : null
    }

    listingField(item) {
        return item.querySelector("[data-hw-combobox-target='hiddenField'][name*='[listing_id]'], select[name*='[listing_id]']")
    }

    listingDisplayField(item) {
        return item.querySelector("[role='combobox']")
    }

    quantityField(item) {
        return item.querySelector("input[name*='[quantity]']")
    }

    unitPriceField(item) {
        return item.querySelector("input[name*='[unit_price]']")
    }

    currentUnitPrice(item, listing = this.selectedListing(item)) {
        const rawValue = this.unitPriceField(item)?.value?.trim()
        if (rawValue && !Number.isNaN(Number(rawValue)) && Number(rawValue) >= 0) return Number(rawValue)

        return listing?.price || 0
    }

    currentDeliveryFee(subtotal) {
        const configuredFee = this.deliveryFeeAmountValue || 0
        const freeShippingThreshold = this.freeShippingThresholdValue || 0

        if (configuredFee <= 0) return 0
        if (freeShippingThreshold > 0 && subtotal >= freeShippingThreshold) return 0

        return configuredFee
    }

    schedulePrimaryFocus() {
        setTimeout(() => {
            if (this.clientSelected()) {
                this.focusFirstQuantityField()
            } else {
                this.clientDisplayField()?.focus()
            }
        }, 10)
    }

    focusFirstQuantityField() {
        const quantityInput = this.items().map(item => this.quantityField(item)).find(Boolean)

        if (!quantityInput) {
            this.clientDisplayField()?.focus()
            return
        }

        quantityInput.focus()
        quantityInput.select?.()
    }

    updateTargetText(targetName, value) {
        const propertyName = `has${targetName.charAt(0).toUpperCase()}${targetName.slice(1)}Target`
        const targetPropertyName = `${targetName}Target`
        if (!this[propertyName]) return

        this[targetPropertyName].textContent = value
    }

    formatCurrency(amount) {
        return Intl.NumberFormat("en-AU", {
            style: "currency",
            currency: this.currencyValue || "AUD"
        }).format(amount)
    }
}
